import { cloneElement, isValidElement, type ReactElement, type ReactNode, useId } from "react";
import { cx } from "../cx.ts";

/** Props a Field hands to its control so the label, hint and error are announced. */
export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  invalid?: boolean;
}

export interface FieldProps {
  label: ReactNode;
  /** Help text under the label. */
  hint?: ReactNode;
  /** Validation message; marks the control invalid. */
  error?: string | undefined;
  /** "(optional)" after the label. */
  optional?: boolean;
  /** Use an existing id instead of a generated one. */
  id?: string;
  className?: string;
  /** One control element, or a function that spreads the control props itself. */
  children: ReactElement<FieldControlProps> | ((props: FieldControlProps) => ReactNode);
}

/**
 * Label, hint and error for one form control, wired with ids so screen
 * readers read all three. Pass the control as the only child (it receives
 * id, aria-describedby and aria-invalid) or as a render function.
 */
export function Field({ label, hint, error, optional, id, className, children }: FieldProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  const controlProps: FieldControlProps = {
    id: controlId,
    ...(describedBy && { "aria-describedby": describedBy }),
    ...(error && { "aria-invalid": true as const, invalid: true }),
  };
  return (
    <div className={cx("flex flex-col gap-1.5", className)}>
      <label
        htmlFor={controlId}
        className="font-medium text-ink text-sm sun:text-min sun:font-bold"
      >
        {label}
        {optional ? <span className="ml-1 font-normal text-ink-muted">(optional)</span> : null}
      </label>
      {hint ? (
        <p id={hintId} className="-mt-1 text-ink-muted text-sm sun:text-min">
          {hint}
        </p>
      ) : null}
      {typeof children === "function"
        ? children(controlProps)
        : isValidElement(children)
          ? cloneElement(children, controlProps)
          : children}
      {error ? (
        <p id={errorId} className="font-medium text-danger text-sm sun:text-min" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
